import pluralize from "pluralize";

import type { CargoItem } from "@features/services/shipping/types";

export const CONTAINER_PRICING_UNITS = ["CBM", "CAR"] as const;
export type ContainerPricingUnit = (typeof CONTAINER_PRICING_UNITS)[number];

export function pricingUnitLabel(
  unit: ContainerPricingUnit,
  count?: number,
): string {
  if (unit === "CBM") {
    return "CBM";
  }
  return count != null ? pluralize("Car", count) : "Car";
}

export function cargoItemFootprint(
  item: CargoItem,
  unit: ContainerPricingUnit,
): number {
  return unit === "CAR" ? item.quantity.value : item.cbm;
}

export function totalCargoFootprint(
  items: CargoItem[],
  unit: ContainerPricingUnit,
): number {
  return items.reduce((sum, item) => sum + cargoItemFootprint(item, unit), 0);
}

export function setCargoItemFootprint(
  item: CargoItem,
  unit: ContainerPricingUnit,
  value: number,
): CargoItem {
  if (unit === "CAR") {
    return { ...item, quantity: { ...item.quantity, value } };
  }
  return { ...item, cbm: value };
}

export function parseFootprintInput(
  text: string,
  unit: ContainerPricingUnit,
): number {
  return unit === "CAR" ? parseInt(text, 10) : parseFloat(text);
}

export interface FootprintFormatters {
  unit: ContainerPricingUnit;
  isCar: boolean;
  unitLabel: string;
  columnLabel: string;
  capacityPlaceholder: string;
  occupiedPlaceholder: string;
  formatItemInput: (value: number) => string;
  formatItemDisplay: (value: number) => string;
  formatTotal: (value: number) => string;
}

const ITEM_DECIMALS = 4;
const TOTAL_DECIMALS = 1;

export function footprintFormatters(
  unit: ContainerPricingUnit,
): FootprintFormatters {
  const isCar = unit === "CAR";
  const unitLabel = pricingUnitLabel(unit);
  return {
    unit,
    isCar,
    unitLabel,
    columnLabel: isCar ? "Quantity" : unitLabel,
    capacityPlaceholder: isCar ? "e.g. 8" : "e.g. 28",
    occupiedPlaceholder: isCar ? "e.g. 4" : "e.g. 14",
    formatItemInput: (value) =>
      isCar ? String(value) : value.toFixed(ITEM_DECIMALS),
    formatItemDisplay: (value) =>
      isCar ? String(Math.round(value)) : `${value.toFixed(ITEM_DECIMALS)} m³`,
    formatTotal: (value) => {
      if (isCar) {
        const rounded = Math.round(value);
        return `${rounded} ${pluralize("car", rounded)}`;
      }
      return `${value.toFixed(TOTAL_DECIMALS)} m³`;
    },
  };
}
